import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PrintNodeOrderItem} from "../../../../model/PrintNodeOrderItem";
import {ManufacturingSetsComponent} from "../../manufacturing-sets/manufacturing-sets.component";
import {ManufacturingSet} from "../../../../model/ManufacturingSet";
import {AssignPrintNodeOrderItemToManufacturingSet} from "../assignPrintNodeOrderItemToManufacturingSet";
import {ManufacturingTaskBucketPeriod} from "../../../../model/ManufacturingTaskBucketPeriod";
import {ManufacturingTaskBucket} from "../../../../model/ManufacturingTaskBucket";
import {PrintNodeOrder} from "../../../../model/PrintNodeOrder";
import {PrintNodeOrderItemService} from "../../../service-data/print-node-order-item.service";
import {MatDialog} from "@angular/material/dialog";
import {SplitPrintNodeOrderComponent} from "../../split-print-node-order/split-print-node-order.component";
import {StartupService} from "../../../service-ui/startup.service";
import {ManufacturingBucketsComponent} from "../manufacturing-buckets.component";
import {PrintNodeOrderService} from "../../../service-data/print-node-order.service";
import {AddFacilityInventoryProductComponent} from "../../facility-inventory-products/add-facility-inventory-product/add-facility-inventory-product.component";
import {PlaceReplenishmentOrderComponent} from "../../facility-inventory-products/facility-inventory-product/place-replenishment-order/place-replenishment-order-component";
import {AddProductToExistingInventoryItemComponent} from "../../facility-inventory-products/facility-inventory-product/add-product-to-existing-inventory-item/add-product-to-existing-inventory-item-component";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {modernDialogConfig} from "../../../service-ui/dialog-config";
import {FacilityInventoryProductService} from "../../../service-data/facility-inventory-product.service";

@Component({
  selector: 'app-print-node-order-item-in-manufacturing-bucket',
  templateUrl: './print-node-order-item-in-manufacturing-bucket.component.html',
  styleUrls: ['./print-node-order-item-in-manufacturing-bucket.component.css']
})
export class PrintNodeOrderItemInManufacturingBucketComponent implements OnInit, OnDestroy {
  @Input() printNodeOrderItem: PrintNodeOrderItem;
  @Input() currentManufacturingSet: ManufacturingSet;
  @Input() currentManufacturingTaskBucketPeriod: ManufacturingTaskBucketPeriod;
  @Input() currentManufacturingTaskBucket: ManufacturingTaskBucket;
  @Input() manufacturingTaskBuckets: [];
  @Input() assignPrintNodeOrderItemToManufacturingSet: AssignPrintNodeOrderItemToManufacturingSet;
  @Input() selectedPrintNodeOrderId: number;
  @Input() facilityInventoryProducts: any;
  @Input() mobileQueryMatchs: boolean;

  facilityInventoryProductsByBookId = {};
  onDestroySubject: Subject<boolean> = new Subject();

  /**
   * True when this item's Book has no FacilityInventoryProduct anywhere
   * AND is stocked-worthy (`inventoryScore > 9`). Gates the "add a new
   * inventory item" button, which is otherwise noise on the thousands of
   * one-off titles that will never be held in stock. Recomputed by
   * `rebuildFacilityInventoryProductIndex()` rather than evaluated from
   * the template, which would re-scan every FIP on every change
   * detection pass for every item on the screen.
   */
  canAddFacilityInventoryProduct: boolean = false;

  /**
   * FacilityInventoryProducts for OTHER Books in the same Project (and
   * organization) as this item's Book. When any exist, this Product can
   * join one of them instead of opening a second inventory item for what
   * the facility treats as one stack of books.
   */
  existingInventoryItemsInSameProject: any[] = [];
  properties = {};
  quantity: number;
  thickness: number;
  createdDate: string;
  pages: number;
  serviceProvider: number;
  oneDigitZip: string;

  units = '';
  types = ManufacturingSetsComponent.types;
  colors = ManufacturingSetsComponent.colors;
  icons = ManufacturingSetsComponent.icons;
  showAddButtons: boolean = false;
  expandActions: boolean = false;
  editSupportNote: boolean = false;
  editDiagnosis: boolean = false;

  // Labels are translation ids and must match rows that exist in
  // src/locale/messages.*.xtb. The bundles carry the Title Case forms
  // ('Fulfill From Inventory', 'Print To Order'); the previous
  // 'Fulfill from Inventory' / 'Print to Order' spellings had no row at all and
  // fell through to the raw literal in every locale.
  inventoryTypeConfigs = {
    MIXED: { label: 'Mixed', icon: 'merge' },
    FULFILL_FROM_INVENTORY: { label: 'Fulfill From Inventory', icon: 'inventory_2' },
    PRINT_TO_ORDER: { label: 'Print To Order', icon: 'precision_manufacturing' },
    REPLENISH_INVENTORY: { label: 'Replenish Inventory', icon: 'autorenew' }
  };

  protected readonly ManufacturingBucketsComponent = ManufacturingBucketsComponent;

  constructor(public startupService: StartupService, private printNodeOrderService: PrintNodeOrderService, private printNodeOrderItemService: PrintNodeOrderItemService, private facilityInventoryProductService: FacilityInventoryProductService, public dialog: MatDialog) { }

  ngOnInit() {
    this.setProperties();
    this.rebuildFacilityInventoryProductIndex();

    // `facilityInventoryProducts` is the parent's array, and the parent
    // maintains it by push/splice from its own subscription to these same
    // notifiers. Mutating an array in place does not change the reference,
    // so `ngOnChanges` can never fire here - which is why a
    // FacilityInventoryProduct created while this screen was open used to
    // leave the "+" button showing and the quantities absent until a full
    // reload. Quantities on an ALREADY indexed item do update on their own
    // (addUpdateLocalItem mutates the cached object in place and the
    // template binds straight to it); what needs rebuilding is the
    // book_ID -> FIP index and the two button gates derived from it.
    //
    // getAddNotifier(false): do NOT flood. The parent floods once for the
    // whole screen; flooding again per order item would replay every
    // FacilityInventoryProduct once per card.
    this.facilityInventoryProductService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(o => {
      this.onFacilityInventoryProductChanged(o);
    });
    this.facilityInventoryProductService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(o => {
      this.onFacilityInventoryProductChanged(o);
    });
    this.facilityInventoryProductService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(o => {
      this.onFacilityInventoryProductChanged(o);
    });
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /**
   * Re-index only when the changed FacilityInventoryProduct could affect
   * THIS order item. Every card on a bucket screen receives every
   * notification, so an unconditional rebuild would be O(cards x FIPs) per
   * event on a screen that routinely shows hundreds of cards.
   */
  private onFacilityInventoryProductChanged(facilityInventoryProduct: any) {
    const book = this.printNodeOrderItem ? this.printNodeOrderItem.book : undefined;
    if ( !book ) { return; }
    const product = facilityInventoryProduct ? facilityInventoryProduct.product : undefined;
    if ( product ) {
      const isThisBook = Number(product.book_ID) === Number(book.ID);
      const projectId = AddProductToExistingInventoryItemComponent.getProjectId(book);
      const isSameProject = !!projectId && !!product.book
        && AddProductToExistingInventoryItemComponent.getProjectId(product.book) === projectId;
      // A FIP for an unrelated Book changes neither the index nor either
      // gate. A FIP for a sibling Book in the same Project changes the
      // "add to an existing inventory item" gate, so it does count.
      if ( !isThisBook && !isSameProject ) { return; }
    }
    // No product attached yet - cannot rule it out, so rebuild.
    this.rebuildFacilityInventoryProductIndex(facilityInventoryProduct);
  }

  setProperties() {
    this.properties['binding'] = this.printNodeOrderItem.book.bindingType;

    const width = this.printNodeOrderItem.book.body.width;
    const height = this.printNodeOrderItem.book.body.height;
    this.properties['trimSize'] = '';
    if ( width / 72 === 5.5 && height / 72 === 8.5 ) {
      this.properties['trimSize'] = '_5.5X8.5';
    } else {
      if ( width / 72 === 6.0 && height / 72 === 9.0 ) {
        this.properties['trimSize'] = '_6.0X9.0';
      } else {
        if ( width / 72 === 8.5 && height / 72 === 11 ) {
          this.properties['trimSize'] = '_8.5X11.0';
        } else {
          this.properties['trimSize'] = 'Other';
        }
      }
    }
    this.properties['impositionLayout'] = width / 72 > 6 || height / 72 > 9 ? '_2UP' : '_4UP';
    this.properties['interior'] = this.printNodeOrderItem.book.interiorType;

    this.quantity = this.printNodeOrderItem.quantity;
    this.thickness = this.printNodeOrderItem.book.body.spine / 72;
    this.pages = this.printNodeOrderItem.book.body.numPages;

    this.properties['paperType'] = String(this.printNodeOrderItem.book.body.paperType_ID);
    this.properties['lamination'] = this.printNodeOrderItem.book.cover.laminateType;

    this.createdDate = '';
    this.serviceProvider = 0;
    this.oneDigitZip = ManufacturingBucketsComponent.getFacilityBasedOnCountryStateAndOneDigitZip(this.printNodeOrderItem.printNodeOrder.country, this.printNodeOrderItem.printNodeOrder.state, this.printNodeOrderItem.printNodeOrder.postalcode ? this.printNodeOrderItem.printNodeOrder.postalcode.substring(0, 1) : '', this.printNodeOrderItem.printNodeOrder.printnode_ID);

    let feetOrSheets = width / 72 > 6 || height / 72 > 9 ? this.quantity * this.pages / 4 : this.quantity * this.pages / 8;
    // Get a criteria to determine that it is comming off of a roll to make it feet
    if ( false ) {
      feetOrSheets = width / 72 > 6 || height / 72 > 9 ? this.quantity * this.pages / 4 : ( this.quantity * this.pages / 6 ) * 10 / 12;
      this.properties['impositionLayout'] =  width / 72 > 6 || height / 72 > 9 ? '_2UP' : '_3UP';
    }

    this.properties['quantityGroup'] = '_001-019';
    if ( this.quantity < 20 ) {
      this.properties['quantityGroup'] = '_001-019';
    } else {
      if ( this.quantity < 50 ) {
        this.properties['quantityGroup'] = '_020-049';
      } else {
        if ( this.quantity < 100 ) {
          this.properties['quantityGroup'] = '_050-099';
        } else {
          if ( this.quantity < 250 ) {
            this.properties['quantityGroup'] = '_100-249';
          } else {
            if ( this.quantity < 500 ) {
              this.properties['quantityGroup'] = '_250-499';
            } else {
              this.properties['quantityGroup'] = '_500+';
            }
          }
        }
      }
    }
  }

  getManufacturingTaskBucketStyle (manufacturingTaskBucket: any): string {
    let style = '';
    switch ( manufacturingTaskBucket['SinglesOrBatch'] ) {
      case 'SINGLES': {
        style += 'border-line: dashed;';
        break;
      }
      case 'BATCH': {
        style += 'border-line: sold;';
        break;
      }
      if ( manufacturingTaskBucket['name'] && manufacturingTaskBucket['name'].indexOf('Simon')) {
        style += 'border-color: green';
      } else {
        style += 'border-color: blue';
      }
    }
    return style;
  }

  /**
   * The inventoryType this item will actually be manufactured under.
   * Delegates to `ManufacturingBucketsComponent.getEffectiveInventoryType` so
   * the item colouring, the set-level indicator counts and the move-target
   * colouring can never disagree about what an item's inventoryType "is".
   */
  getCurrentInventoryType (printNodeOrderItem: PrintNodeOrderItem): string {
    return ManufacturingBucketsComponent.getEffectiveInventoryType(printNodeOrderItem);
  }

  isInventoryTypeChangeNeeded(printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet, manufacturingTaskBucket: ManufacturingTaskBucket): boolean {
    const currentInventoryType = this.getCurrentInventoryType(printNodeOrderItem);
    if ( manufacturingSet ) {
      if ( manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === currentInventoryType) {
        return false;
      } else {
        return true;
      }
    } else {
      if ( manufacturingTaskBucket ) {
        if ( manufacturingTaskBucket.name && manufacturingTaskBucket.name.indexOf('Inventory') > -1 ) { // TODO: Replace this with an inventoryType property on ManufacturingTaskBucket
          if ( currentInventoryType === 'FULFILL_FROM_INVENTORY' ) {
            return false;
          }
        }
      }
      return false;
    }
  }

  doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets(): boolean {
    if ( this.printNodeOrderItem.printNodeOrder.hasOwnProperty('areAnyPrintNodeOrderItemsInClosedSets')) { // This is not a public property but it is being set on the fly to save processing time. It is being set in this function and on the PrintNodeOrder when first checked
      return this.printNodeOrderItem.printNodeOrder['areAnyPrintNodeOrderItemsInClosedSets'];
    }
    this.printNodeOrderItem.printNodeOrder['areAnyPrintNodeOrderItemsInClosedSets'] = false; // If true then no moves of anything to a different facility

    for ( const pnoi of this.printNodeOrderItem.printNodeOrder.printNodeOrderItems ) { // I don't like this because it is not very efficient
      if ( pnoi.manufacturingSet && pnoi.manufacturingSet.openStatus === 'CLOSED' ) {
        this.printNodeOrderItem.printNodeOrder['areAnyPrintNodeOrderItemsInClosedSets'] = true;
        break;
      }
    }
    return this.printNodeOrderItem.printNodeOrder['areAnyPrintNodeOrderItemsInClosedSets'];
  }

  noOpenManufacturingTaskBucketPeriods(manufacturingTaskBucket: ManufacturingTaskBucket): boolean {
    for ( const manufacturingTaskBucketPeriod of manufacturingTaskBucket.manufacturingTaskBucketPeriods ) {
      if ( manufacturingTaskBucketPeriod.status === 'OPEN' ) {
        return false;
      }
    }
    return true;
  }

  // TODO: Revisit this logic
  showManufacturingTaskBucketPeriod (manufacturingTaskBucketPeriod: ManufacturingTaskBucketPeriod): boolean {
    try {
      if ( manufacturingTaskBucketPeriod.status === 'CLOSED') {
        return false;
      }

      const doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets = this.doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets();
      // If there closed sibling PrintNodeOrderItems and the ManufacturingSet is in a different facility than the PrintNodeOrderItem then it can't be moved there because the siblings can't be split up
      if ( !this.printNodeOrderItem || !this.printNodeOrderItem.printNodeOrder || !this.printNodeOrderItem.printNodeOrder.printnode_ID ) {
        return false;
      }
      let manufacturingTaskBucketPrintNodeId = 0;
      if ( !manufacturingTaskBucketPeriod || !manufacturingTaskBucketPeriod.manufacturingTaskBucket || !manufacturingTaskBucketPeriod.manufacturingTaskBucket.printnode_ID ) {
        if ( manufacturingTaskBucketPeriod.manufacturingSets && manufacturingTaskBucketPeriod.manufacturingSets.length > 0 ) {
          manufacturingTaskBucketPrintNodeId = manufacturingTaskBucketPeriod.manufacturingSets[0].printnode_id;
        }
      } else {
        manufacturingTaskBucketPrintNodeId     = manufacturingTaskBucketPeriod.manufacturingTaskBucket.printnode_ID;
      }
      if ( doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets && this.printNodeOrderItem && this.printNodeOrderItem.printNodeOrder && manufacturingTaskBucketPeriod && manufacturingTaskBucketPeriod.manufacturingTaskBucket && this.printNodeOrderItem.printNodeOrder.printnode_ID !== manufacturingTaskBucketPrintNodeId ) {
        return false;
      }
      return true;
    } catch ( e ) {
      console.error('Error in showManufacturingTaskBucketPeriod:', e);
      return false;
    }
  }

  // look at passing in no manufacturingSet and the inventoryType singleOrBatch value to determine if the move to a ManufacturingTaskBucketPeriod can be done
  // Break this function into isManufacturingSetAnOption independent from can it be split from its siblings and the details of changing facilites and inventoryType values
  // Can PrintNodeItem be moved because of sibling PrintNodeOrderItems already in closed Sets
  // What has to be changed in the inventoryType values
  // What has to be changed in the facility assignment
  showManufacturingSet (printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet): boolean {
    if ( printNodeOrderItem.manufacturingset_id === manufacturingSet.ID ) {
      return false;
    }
    if ( manufacturingSet.openStatus === 'CLOSED' ) {
      return false;
    }

    const doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets = this.doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets();
    // If there closed sibling PrintNodeOrderItems and the ManufacturingSet is in a different facility than the PrintNodeOrderItem then it can't be moved there because the siblings can't be split up
    if ( doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets ) {
      if ( !printNodeOrderItem || !printNodeOrderItem.printNodeOrder || !printNodeOrderItem.printNodeOrder.printnode_ID ) {
        return false;
      }
      if ( doesPrintNodeOrderItemHaveSiblingsInClosedManufacturingSets && printNodeOrderItem.printNodeOrder.printnode_ID !== manufacturingSet.printnode_id ) { // Can't go to this set because there are other printNodeOrderItems in closed sets and this ManufacturingSet is in a different facility
        return false;
      }
    }

    // If the set is an inventory set
    if ( manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === 'FULFILL_FROM_INVENTORY') {
      const printNodeOrder = printNodeOrderItem.printNodeOrder;
      if (printNodeOrderItem.printNodeOrder.inventoryType === 'REPLENISH_INVENTORY') {
        return false;
      }
      const manufacturingSetInventoryGrouping = manufacturingSet.details && manufacturingSet.details.inventoryGrouping ? manufacturingSet.details.inventoryGrouping : '';
      if ( manufacturingSetInventoryGrouping === '' + printNodeOrderItem.book_ID && printNodeOrderItem.printNodeOrder.printNodeOrderItems.length === 1 ) {
        return true;
      }
      if ( manufacturingSetInventoryGrouping === 'Org-' + printNodeOrder.org_ID + '-FULFILL_FROM_INVENTORY') {
        return true;
      }
      if ( manufacturingSetInventoryGrouping === 'Org-' + printNodeOrder.org_ID + '-MIXED' &&  printNodeOrderItem.printNodeOrder.printNodeOrderItems.length !== 1) {
        return true;
      }
      return false;
    } else {
      // Print to order set
      if (manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === 'PRINT_TO_ORDER') {
        if (manufacturingSet.details.interiorType && printNodeOrderItem.book.interiorType !== manufacturingSet.details.interiorType) {
          return false;
        }
        if (manufacturingSet.details.bindingType && printNodeOrderItem.book.bindingType !== manufacturingSet.details.bindingType) {
          return false;
        }
        if ( !this.isSamePaper(printNodeOrderItem, manufacturingSet ) ) {
          return false;
        }
        if (printNodeOrderItem.book.cover.laminateType !== manufacturingSet.details.laminateType) {
          return false;
        }
        if (this.getNumberUp(printNodeOrderItem) !== manufacturingSet.details.numberUp) {
          return false;
        }
        return true;
      }
    }
  }

  // showManufacturingSet (printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet): boolean {
  //   if ( printNodeOrderItem.manufacturingset_id === manufacturingSet.ID ) {
  //     return false;
  //   }
  //   if ( manufacturingSet.openStatus === 'CLOSED' ) {
  //     return false;
  //   }
  //   if ( manufacturingSet.printNodeOrderItems.length === 0 ) {
  //     return true;
  //   }
  //   if ( manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === 'FULFILL_FROM_INVENTORY') {
  //     return false;
  //   } else {
  //     if (manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === 'PRINT_TO_ORDER') {
  //       if (manufacturingSet.details.interiorType && printNodeOrderItem.book.interiorType !== manufacturingSet.details.interiorType) {
  //         return false;
  //       }
  //       if (manufacturingSet.details.bindingType && printNodeOrderItem.book.bindingType !== manufacturingSet.details.bindingType) {
  //         return false;
  //       }
  //       if (printNodeOrderItem.book.cover.laminateType !== manufacturingSet.details.laminateType) {
  //         return false;
  //       }
  //
  //       if (this.getNumberUp(printNodeOrderItem) !== manufacturingSet.details.numberUp) {
  //         return false;
  //       }
  //     }
  //   }
  //   return true;
  // }
  getAssignToSpecificManufacturingSetColor(printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet): string {
    const sameInventoryType = manufacturingSet.details.inventoryType && manufacturingSet.details.inventoryType === this.getCurrentInventoryType(printNodeOrderItem);
    const sameFacility = manufacturingSet.printnode_id === printNodeOrderItem.printNodeOrder.printnode_ID;

    if ( sameInventoryType && sameFacility ) {
        return 'lightgreen';
    }
    if ( sameInventoryType && !sameFacility ) {
      return 'lightyellow';
    }
    if ( !sameInventoryType && sameFacility ) {
      return 'lightblue';
    }
    if ( !sameInventoryType && !sameFacility ) {
      return 'orange';
    }
    // return 'lightblue';
    // if ( this.isSameTrimSize(printNodeOrderItem, manufacturingSet)) {
    //   return 'lightgreen';
    // } else {
    //   return 'lightorange';
    // }
  }

  getItems(manufacturingSet: ManufacturingSet): number {
    return manufacturingSet.printNodeOrderItems.length;
  }

  getQuantity(manufacturingSet: ManufacturingSet): number {
    let quantity = 0;
    for ( const printNodeOrderItem of manufacturingSet.printNodeOrderItems) {
      quantity += printNodeOrderItem.quantity;
    }
    return quantity;
  }

  isSameTrimSize(printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet): boolean {
    // if ( this.getTrimSize(printNodeOrderItem) !== manufacturingSet.details.trimSize) {
    //   return false;
    // }
    return true;
  }

  isSamePaper(printNodeOrderItem: PrintNodeOrderItem, manufacturingSet: ManufacturingSet): boolean {
    if ( printNodeOrderItem.book.body.paperType_ID === manufacturingSet.details.interiorPaperType) {
      return true;
    }
    return false;
  }

  showManufacturingTaskBucket (printNodeOrderItem: PrintNodeOrderItem, manufacturingTaskBucket: any): boolean {
    if ( manufacturingTaskBucket['printnode_ID'] === 8) {
      return false;
    }
    if ( manufacturingTaskBucket['printnode_ID'] === 5) { // TODO: Change this to check if the facility is a fulfillment one or not and THEN check to see if it can fulfill this order
      return false;
    }
    if ( manufacturingTaskBucket['status'] === 'CLOSED') {
      return false;
    }
    if ( ( manufacturingTaskBucket.printnode_ID === 1 || manufacturingTaskBucket.printnode_ID === 6 ) || ( printNodeOrderItem.quantity < 20 && manufacturingTaskBucket['SinglesOrBatch'] === 'SINGLES' ) ||  ( printNodeOrderItem.quantity > 14 && manufacturingTaskBucket['SinglesOrBatch'] === 'BATCH' ) ) {
      return true;
    }
    return false;
  }

  getNumberUp (printNodeOrderItem: PrintNodeOrderItem): number {
    if ( printNodeOrderItem.book.body.width / 72 > 6 || printNodeOrderItem.book.body.height / 72 > 9 ) {
      return 2;
    }
    return 4;
  }

  getTrimSize (printNodeOrderItem: PrintNodeOrderItem): string {
    const width = Math.round(printNodeOrderItem.book.body.width / 72 * 100) / 100;
    const height = Math.round(printNodeOrderItem.book.body.height / 72 * 100) / 100;
    if ( this.fuzzyEquals(width, 8.50) && this.fuzzyEquals(height, 11.00)) {
      return '8.50x11.00';
    }
    if ( this.fuzzyEquals(width, 6.00) && this.fuzzyEquals(height, 9.00)) {
      return '6.00x9.00';
    }
    if ( this.fuzzyEquals(width, 5.50) && this.fuzzyEquals(height, 8.50)) {
      return '5.50x8.50';
    }
    if ( this.fuzzyEquals(width, 8.25) && this.fuzzyEquals(height, 10.75)) {
      return '8.25 x 10.75';
    }
    if ( this.fuzzyEquals(width, 5.00) && this.fuzzyEquals(height,  8.00)) {
      return '5.00x8.00';
    }
    if ( this.fuzzyEquals(width, 7.00) && this.fuzzyEquals(height, 10.00)) {
      return '7.00x10.00';
    }
    if ( this.fuzzyEquals(width, 5.25) && this.fuzzyEquals(height, 8.25)) {
      return '5.25x8.25';
    }
    return '---';
  }

  fuzzyEquals(a: number, b: number): boolean {
    if ( Math.abs( a - b ) <= 0.055 ) {
      return true;
    }
    return false;
  }

  canMoveToFulfillment (printNodeOrder: PrintNodeOrder): boolean {
    return true;
  }

  canMoveToSpecificFulfillmentManufacturingTaskBucket (printNodeOrder: PrintNodeOrder, manufacturingTaskBucket: ManufacturingTaskBucket): boolean {
    return false;
  }

  saveSupportNote () {
    const localPrintNodeOrderItem = new PrintNodeOrderItem(this.printNodeOrderItem, this.printNodeOrderItem.printNodeOrder);
    this.printNodeOrderItemService.updateItem(localPrintNodeOrderItem).subscribe( newPrintNodeOrderItem => {
      console.log(`Updated printNodeOrderItem Support Info`);
    });
  }

  cancelSupportNote () {
    this.printNodeOrderItem.supportNote = '';
  }


  isDateOld(date: Date): boolean {
    try {
      const d = new Date(date);
      const now = new Date();
      const milliSecondsOld = now.getTime() - d.getTime();
      if ( milliSecondsOld > 2 * 24 * 60 * 60 * 1000) {
        return true;
      }
    } catch ( e ) {
      console.log(`isDateOld Error`);
    }
    return false;
  }

  splitPrintNodeOrder (printNodeOrder: PrintNodeOrder) {
    const dialogConfig = modernDialogConfig();
    dialogConfig.width = this.mobileQueryMatchs ? '600px' : '95%';
    dialogConfig.height = this.mobileQueryMatchs ? '700px' : '95%';
    dialogConfig.disableClose = false;
    dialogConfig.data = { printNodeOrder: printNodeOrder, mobileQueryMatches: this.mobileQueryMatchs };
    const dialogRef = this.dialog.open(SplitPrintNodeOrderComponent, dialogConfig);
    if ( dialogRef ) {
    }
  }

  /**
   * Open the Add Facility Inventory Product dialog pre-filled with this
   * item's book and target facility so the operator can create a
   * FacilityInventoryProduct for the item without having to look it up.
   */
  addFacilityInventoryProductForItem() {
    const dialogConfig = modernDialogConfig();
    dialogConfig.width = '720px';
    dialogConfig.disableClose = false;
    dialogConfig.data = {
      bookId: this.printNodeOrderItem.book.ID,
      printnode_ID: this.printNodeOrderItem.printNodeOrder.printnode_ID
    };
    const dialogRef = this.dialog.open(AddFacilityInventoryProductComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      // Refresh the local index so the new FIP shows up next to the item.
      this.rebuildFacilityInventoryProductIndex();
    });
  }

  /**
   * Offer this item's Product to an existing FacilityInventoryProduct
   * held for another Book in the same Project. See
   * `AddProductToExistingInventoryItemComponent` for why that is
   * preferable to a second inventory item.
   */
  addProductToExistingInventoryItemForItem() {
    const dialogConfig = modernDialogConfig();
    dialogConfig.width = '640px';
    dialogConfig.disableClose = false;
    dialogConfig.data = {
      bookId: this.printNodeOrderItem.book_ID,
      book: this.printNodeOrderItem.book,
      facilityInventoryProducts: this.facilityInventoryProducts
    };
    const dialogRef = this.dialog.open(AddProductToExistingInventoryItemComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      this.rebuildFacilityInventoryProductIndex();
    });
  }

  /**
   * Index `facilityInventoryProducts` by `product.book_ID` and re-derive
   * the two button gates from it.
   *
   * The index is keyed on the FIP's *current* product, which is why a
   * Product that was merely appended to another item's `productIds` does
   * not make this Book look stocked - that is correct: the stock on the
   * shelf belongs to the Book the inventory item is for.
   */
  private rebuildFacilityInventoryProductIndex(extraFacilityInventoryProduct?: any) {
    this.facilityInventoryProductsByBookId = {};
    const seenIds = {};
    const index = (facilityInventoryProduct: any) => {
      if ( !facilityInventoryProduct || !facilityInventoryProduct.product ) { return; }
      // De-duplicate by id: the same object can legitimately reach this
      // twice (once from the parent's array, once as `extra`), and a
      // duplicate here renders the inventory row twice.
      if ( seenIds['' + facilityInventoryProduct.ID] ) { return; }
      seenIds['' + facilityInventoryProduct.ID] = true;
      const key = '' + facilityInventoryProduct.product.book_ID;
      if ( !this.facilityInventoryProductsByBookId[key] ) {
        this.facilityInventoryProductsByBookId[key] = [];
      }
      this.facilityInventoryProductsByBookId[key].push(facilityInventoryProduct);
    };
    (this.facilityInventoryProducts || []).forEach(index);
    // The parent subscribes to the notifier before this component exists,
    // so its array is normally already up to date by the time we rebuild.
    // Indexing the changed object directly removes the dependency on that
    // ordering without mutating an array this component does not own.
    if ( extraFacilityInventoryProduct ) { index(extraFacilityInventoryProduct); }

    const book = this.printNodeOrderItem ? this.printNodeOrderItem.book : undefined;
    const bookId = book ? book.ID : 0;
    const alreadyStocked = !!this.facilityInventoryProductsByBookId['' + bookId];
    this.canAddFacilityInventoryProduct = !alreadyStocked && Number((book && book.inventoryScore) || 0) > 9;
    this.existingInventoryItemsInSameProject = alreadyStocked
      ? []
      : AddProductToExistingInventoryItemComponent.findCandidates(book, bookId, this.facilityInventoryProducts || []);
  }

  /**
   * Open the existing Place Replenishment Order dialog for a specific
   * FacilityInventoryProduct. Mirrors
   * FacilityInventoryProductComponent.placeReplenishmentOrder().
   */
  placeReplenishmentOrderForFip(facilityInventoryProduct: any) {
    if ( !facilityInventoryProduct ) { return; }
    const dialogConfig = modernDialogConfig();
    dialogConfig.width = '640px';
    dialogConfig.disableClose = false;
    dialogConfig.data = { facilityInventoryProduct: facilityInventoryProduct };
    this.dialog.open(PlaceReplenishmentOrderComponent, dialogConfig);
  }

  // InventoryType changes
  get currentInventoryType() {
    return this.inventoryTypeConfigs[this.printNodeOrderItem.printNodeOrder.inventoryType];
  }

  /**
   * MIXED only means anything when there is more than one PrintNodeOrderItem to
   * be mixed - a single-item PrintNodeOrder that is MIXED just pushes the real
   * inventoryType down a level for no benefit and lights up the item-level
   * warning states. So do not offer it when the order holds one item (or fewer).
   */
  get isSinglePrintNodeOrderItemOrder(): boolean {
    const printNodeOrder = this.printNodeOrderItem ? this.printNodeOrderItem.printNodeOrder : null;
    const printNodeOrderItems = printNodeOrder && printNodeOrder.printNodeOrderItems ? printNodeOrder.printNodeOrderItems : [];
    return printNodeOrderItems.length <= 1;
  }

  // List of other options
  get otherInventoryTypeOptions() {
    const currentPrintNodeOrderInventoryType = this.printNodeOrderItem.printNodeOrder.inventoryType;
    const hideMixed = this.isSinglePrintNodeOrderItemOrder && currentPrintNodeOrderInventoryType !== 'MIXED';
    return Object.keys(this.inventoryTypeConfigs)
      .filter(t => t !== currentPrintNodeOrderInventoryType)
      .filter(t => !(hideMixed && t === 'MIXED')) as Array<keyof typeof this.inventoryTypeConfigs>;
  }

  changeInventoryType(newPrintNodeOrderInventoryType: keyof typeof this.inventoryTypeConfigs) {
    const printNodeOrder = this.printNodeOrderItem.printNodeOrder;
    const previousPrintNodeOrderInventoryType = printNodeOrder.inventoryType;
    printNodeOrder.inventoryType = newPrintNodeOrderInventoryType;
    console.log('PrintNodeOrder InventoryType changed to:', newPrintNodeOrderInventoryType);
    this.printNodeOrderService.updateItem(printNodeOrder).subscribe( newPrintNodeOrder => {
      console.log(`Updated printNodeOrder`);
    });
    // Switching the order to MIXED hands responsibility for the inventoryType
    // down to the individual items, but until now nothing populated them — the
    // items kept whatever they happened to hold (usually ''), so every one of
    // them immediately rendered the warning triangle and had to be set by hand.
    // Seed them with the value the order itself was carrying a moment ago,
    // which is by definition how all of them were going to be manufactured.
    if ( newPrintNodeOrderInventoryType === 'MIXED' && previousPrintNodeOrderInventoryType !== 'MIXED' ) {
      this.seedPrintNodeOrderItemInventoryTypes(printNodeOrder, previousPrintNodeOrderInventoryType);
    }
  }

  /**
   * Copy `previousPrintNodeOrderInventoryType` down onto every
   * PrintNodeOrderItem of `printNodeOrder` and persist the ones that actually
   * changed.
   *
   * The order-level value is normalized onto the item-level vocabulary first
   * (REPLENISH_INVENTORY orders are printed, so their items are PRINT_TO_ORDER
   * — the same mapping `getCurrentInventoryType` has always used). If the
   * previous value does not normalize to a valid item value there is nothing
   * meaningful to copy and the items are left alone for the operator to set.
   */
  private seedPrintNodeOrderItemInventoryTypes(printNodeOrder: PrintNodeOrder, previousPrintNodeOrderInventoryType: string) {
    const seedInventoryType = ManufacturingBucketsComponent.normalizeInventoryType(previousPrintNodeOrderInventoryType);
    if ( !ManufacturingBucketsComponent.isItemInventoryTypeValid(seedInventoryType) ) {
      console.log(`Not seeding PrintNodeOrderItem inventoryType values - previous PrintNodeOrder inventoryType [${previousPrintNodeOrderInventoryType}] has no item-level equivalent`);
      return;
    }
    const printNodeOrderItems = printNodeOrder.printNodeOrderItems ? printNodeOrder.printNodeOrderItems : [];
    for ( const printNodeOrderItem of printNodeOrderItems ) {
      if ( !printNodeOrderItem || printNodeOrderItem.inventoryType === seedInventoryType ) {
        continue; // Already correct - do not spend a round trip re-saving it
      }
      printNodeOrderItem.inventoryType = seedInventoryType;
      this.printNodeOrderItemService.updateItem(printNodeOrderItem).subscribe( updatedPrintNodeOrderItem => {
        console.log(`Seeded printNodeOrderItem ${printNodeOrderItem.ID} inventoryType to ${seedInventoryType}`);
      });
    }
  }

  changePrintNodeOrderItemInventoryType(newPrintNodeOrderItemInventoryType: keyof typeof this.inventoryTypeConfigs) {
    this.printNodeOrderItem.inventoryType = newPrintNodeOrderItemInventoryType;
    console.log('PrintNodeOrderItem InventoryType changed to:', newPrintNodeOrderItemInventoryType);
    this.printNodeOrderItemService.updateItem(this.printNodeOrderItem).subscribe( newPrintNodeOrderItem => {
      console.log(`Updated printNodeOrderItem`);
    });
  }

  // ---------------------------------------------------------------------------
  // inventoryType warning colouring
  //
  // Two conditions, and they are not the same problem:
  //
  //   ORANGE — the item's own inventoryType is neither FULFILL_FROM_INVENTORY
  //            nor PRINT_TO_ORDER. This is the state the warning triangle
  //            already indicated; the background makes it visible without
  //            having to read the small icon. Nobody has decided how this item
  //            gets made yet.
  //   RED    — the item HAS a valid inventoryType but it disagrees with the
  //            ManufacturingSet and/or ManufacturingTaskBucket it is sitting
  //            in. A decision was made and the item is in the wrong place.
  //
  // Orange wins when both could apply, because an unset value cannot really be
  // said to "disagree" with anything — `isPrintNodeOrderItemInventoryTypeMismatched`
  // returns false in that case anyway, so the two are mutually exclusive by
  // construction. The set-level indicator in manufacturing-buckets.component.html
  // counts exactly the RED items.
  // ---------------------------------------------------------------------------

  /** True when this item's own inventoryType is missing or unrecognized. */
  isPrintNodeOrderItemInventoryTypeInvalid(): boolean {
    return !ManufacturingBucketsComponent.isItemInventoryTypeValid(this.printNodeOrderItem.inventoryType);
  }

  /**
   * True when the way this item is actually going to be manufactured disagrees
   * with the ManufacturingSet or ManufacturingTaskBucket holding it.
   */
  isInventoryTypeMismatchedWithSetOrBucket(): boolean {
    return ManufacturingBucketsComponent.isPrintNodeOrderItemInventoryTypeMismatched(this.printNodeOrderItem, this.currentManufacturingSet, this.currentManufacturingTaskBucket);
  }

  /** Warning level for the per-item inventoryType control (only shown on MIXED orders). */
  getPrintNodeOrderItemInventoryTypeWarningLevel(): string {
    if ( this.isPrintNodeOrderItemInventoryTypeInvalid() ) {
      return 'INVALID';
    }
    if ( this.isInventoryTypeMismatchedWithSetOrBucket() ) {
      return 'MISMATCH';
    }
    return '';
  }

  /**
   * Warning level for the order-level inventoryType control. Only reports
   * MISMATCH for non-MIXED orders — on a MIXED order the order-level value is
   * not what put the item in the wrong set, so the red belongs on the per-item
   * control next to it rather than on both.
   */
  getPrintNodeOrderInventoryTypeWarningLevel(): string {
    if ( this.printNodeOrderItem.printNodeOrder.inventoryType === 'MIXED' ) {
      return '';
    }
    if ( this.isInventoryTypeMismatchedWithSetOrBucket() ) {
      return 'MISMATCH';
    }
    return '';
  }

  /** Tooltip explaining whichever inventoryType warning colour is currently applied. */
  getPrintNodeOrderItemInventoryTypeTitle(): string {
    if ( this.isPrintNodeOrderItemInventoryTypeInvalid() && this.printNodeOrderItem.printNodeOrder.inventoryType === 'MIXED' ) {
      return LanguagePipe.get('Inventory Type Has Not Been Set For This Item');
    }
    if ( this.isInventoryTypeMismatchedWithSetOrBucket() ) {
      return LanguagePipe.get('Item Inventory Type Does Not Match Its Set') + ': '
        + this.getInventoryTypeLabel(ManufacturingBucketsComponent.getEffectiveInventoryType(this.printNodeOrderItem)) + ' / '
        + this.getInventoryTypeLabel(ManufacturingBucketsComponent.getManufacturingSetInventoryType(this.currentManufacturingSet));
    }
    return LanguagePipe.get('Inventory Type');
  }

  /** Translated display label for an inventoryType enum token. */
  getInventoryTypeLabel(inventoryType: string): string {
    const label = ManufacturingBucketsComponent.INVENTORY_TYPE_LABELS[inventoryType];
    return LanguagePipe.get(label ? label : 'Not Set');
  }

  /**
   * Return every `PrintNodeOrderItem` in the same `PrintNodeOrder`
   * as this item that is currently assigned to
   * `currentManufacturingSet` — i.e. the same-order siblings that
   * share this set. Includes the item itself. Used to gate and
   * drive the "Move Order's Items In This Set To A New Set" button:
   * the button only makes sense when there are at least two
   * same-order items sharing the set (otherwise it collapses to
   * the existing single-item "Move To Bucket and Put In A New Set"
   * affordance and would just be visual noise).
   */
  getSameOrderPrintNodeOrderItemsInCurrentSet(): PrintNodeOrderItem[] {
    if (!this.printNodeOrderItem
        || !this.printNodeOrderItem.printNodeOrder
        || !this.printNodeOrderItem.printNodeOrder.printNodeOrderItems
        || !this.currentManufacturingSet) {
      return [];
    }
    const currentSetId = this.currentManufacturingSet.ID;
    const siblings: PrintNodeOrderItem[] = [];
    for (const pnoi of this.printNodeOrderItem.printNodeOrder.printNodeOrderItems) {
      if (pnoi && pnoi.manufacturingset_id === currentSetId) {
        siblings.push(pnoi);
      }
    }
    return siblings;
  }

  /**
   * True when the current ManufacturingSet contains two or more
   * `PrintNodeOrderItem`s from the same `PrintNodeOrder` as this
   * item. Drives the visibility of the sibling-move button.
   */
  canMoveSameOrderSiblingsToNewSet(): boolean {
    return this.getSameOrderPrintNodeOrderItemsInCurrentSet().length > 1;
  }

  /**
   * True when the items in `currentManufacturingSet` are not all
   * homogeneous with respect to trim size, interior paper type,
   * cover lamination type, or binding type. Used to highlight the
   * per-item thickness / stacker height display in pink so the
   * operator can see at a glance that this set is mixed.
   */
  hasMixedSetProperties(): boolean {
    if (!this.currentManufacturingSet
        || !this.currentManufacturingSet.printNodeOrderItems
        || this.currentManufacturingSet.printNodeOrderItems.length < 2) {
      return false;
    }
    const items = this.currentManufacturingSet.printNodeOrderItems;
    let firstTrim: string = null;
    let firstPaper: any = null;
    let firstLam: any = null;
    let firstBind: any = null;
    let initialized = false;
    for (const it of items) {
      if (!it || !it.book || !it.book.body || !it.book.cover) { continue; }
      const trim = this.getTrimSize(it);
      const paper = it.book.body.paperType_ID;
      const lam = it.book.cover.laminateType;
      const bind = it.book.bindingType;
      if (!initialized) {
        firstTrim = trim;
        firstPaper = paper;
        firstLam = lam;
        firstBind = bind;
        initialized = true;
        continue;
      }
      if (trim !== firstTrim) { return true; }
      if (paper !== firstPaper) { return true; }
      if (lam !== firstLam) { return true; }
      if (bind !== firstBind) { return true; }
    }
    return false;
  }
}
